import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useDatabaseService, type OrganizationProfilePatch } from '@/services/databaseService'
import { useBackendService } from '@/services/backendService'
import { useUserStore } from '@/stores/user'
import { useSuperAdmin } from '@/composables/use-super-admin'
import { storeToRefs } from 'pinia'
import type { Organization, Member } from '@/types/organization'

export const useOrganizationsStore = defineStore('organizations', () => {
  const databaseService = useDatabaseService()
  const backendService = useBackendService()
  const { user } = storeToRefs(useUserStore())
  const { isSuperAdmin, setSuperAdmin } = useSuperAdmin()

  const organizations = ref<Organization[]>([])
  const allOrganizations = ref<Organization[]>([])
  const activeOrganization = ref<Organization | null>(null)
  const members = ref<Member[]>([])
  // Whose caller context (super-admin flag + `organizations`) is loaded. The
  // route guard reads it on every navigation, so it is fetched once per
  // session: resetOrganizations() clears it on sign-out or a user switch.
  const callerContextUserId = ref<string | null>(null)

  // The route guard hands out rows from the cached lists, so an edit to the
  // active organization (logo, welcome message, benefits) is written back
  // into them; otherwise the next navigation would restore the pre-edit row.
  watch(
    activeOrganization,
    (org) => {
      if (!org) return
      for (const list of [organizations.value, allOrganizations.value]) {
        const i = list.findIndex((o) => o.organization_id === org.organization_id)
        if (i >= 0 && list[i] !== org) list[i] = { ...list[i], ...org }
      }
    },
    { flush: 'sync' },
  )

  // Get the current user's role in the active organization
  const currentUserRole = computed(() => {
    if (!user.value?.id || !members.value?.length) return null
    const currentMember = members.value.find(m => m.user_id === user.value!.id)
    return currentMember?.role || null
  })

  // Check if current user can manage member roles (owner, admin, or super admin)
  const canManageRoles = computed(() => {
    if (isSuperAdmin.value) return true
    return currentUserRole.value === 'owner' || currentUserRole.value === 'admin'
  })

  const resetOrganizations = () => {
    organizations.value = []
    allOrganizations.value = []
    activeOrganization.value = null
    members.value = []
    callerContextUserId.value = null
  }

  const setOrganizations = (data: Organization[]) => {
    organizations.value = data
  }

  const setActiveOrganization = (data: Organization | null) => {
    activeOrganization.value = data
  }

  // One GET /admin/me per session instead of five Supabase reads per
  // navigation. `refresh` re-reads it, for a slug the cached list lacks
  // (an organization granted or renamed since the session started).
  const loadCallerContext = async (userId: string, { refresh = false } = {}) => {
    if (!refresh && callerContextUserId.value === userId) return
    const me = await backendService.getAdminMe()
    organizations.value = me.organizations
    setSuperAdmin(me.is_super_admin)
    callerContextUserId.value = userId
  }

  const updateActiveOrganization = async (patch: OrganizationProfilePatch) => {
    if (!activeOrganization.value?.organization_id) {
      throw new Error('No active organization selected')
    }
    const updated = await databaseService.updateOrganization(
      activeOrganization.value.organization_id,
      patch,
    )
    activeOrganization.value = { ...activeOrganization.value, ...updated }
    return updated
  }

  const resolveOrgBySlug = (slug: string) => {
    return organizations.value.find((o) => o.slug === slug) || null
  }

  const setMembers = (value: Member[]) => {
    members.value = value
  }

  const getMembers = async () => {
    if (!activeOrganization.value?.organization_id) {
      throw new Error('No active organization selected')
    }

    const data = await databaseService.getMembers(activeOrganization.value.organization_id)
    setMembers(data)
  }

  const updateMemberRole = async (memberId: number, newRole: 'member' | 'admin') => {
    await databaseService.updateMemberRole(memberId, newRole)
    // Refresh members list to get updated data
    await getMembers()
  }

  // ========== SuperAdmin Operations ==========

  const getAllOrganizations = async () => {
    const data = await databaseService.getAllOrganizations()
    allOrganizations.value = data
  }

  const deleteOrganization = async (organizationId: string) => {
    await databaseService.deleteOrganization(organizationId)
    await getAllOrganizations()
    // Also clean up the regular organizations list
    organizations.value = organizations.value.filter(
      o => o.organization_id !== organizationId
    )
    if (activeOrganization.value?.organization_id === organizationId) {
      activeOrganization.value = null
      members.value = []
    }
  }

  return {
    organizations,
    activeOrganization,
    members,
    currentUserRole,
    canManageRoles,
    resetOrganizations,
    getMembers,
    setMembers,
    setActiveOrganization,
    setOrganizations,
    loadCallerContext,
    updateActiveOrganization,
    resolveOrgBySlug,
    updateMemberRole,
    allOrganizations,
    getAllOrganizations,
    deleteOrganization,
  }
})
